<template>
    <div class="device_detail_main_card_bottom">
        <div class="device_detail_main_card_bottom_left" >
            <p><span class="device_detail_main_card_bottom_left_title">
                   {{ $t('dashboard.device_detail.auto_renewal') }}
               </span>
                <ReoLink
                    :text="$t('dashboard.device_detail.details')"
                    href="/subscription-terms/"
                    :show-icon="false"
                    :size="isMobile ? 14 : 16"
                    target="_blank"
                />
            </p>
            <Transition name="fade">
                <p class="transition device_detail_main_card_bottom_left_content" v-if="autoRenewal">{{ $t('dashboard.device_detail.next_bill') }}{{ nextBillingAtFormatted }}</p>
            </Transition>

        </div>
        <div class="device_detail_main_card_bottom_right">
            <ElSwitch :value="autoRenewal"
                active-color="var(--b_6)"
                :width="36"
                @change="handleAutoRenewChange"/>
            <ReoDialog
                v-if="showItems.showDialogs.disableAutoRenew"
                close-icon-offset="20px"
                :title="$t('dashboard.device_detail.main_card.disable_renew_title')"
                :description="$t('dashboard.device_detail.main_card.disable_renew_content')"
                @confirm="disableAutoRenewal"
                @cancel="showItems.showDialogs.disableAutoRenew=false;"
                :label-of-confirm="$t('dashboard.device_detail.main_card.disable_renew_yes')"
                :label-of-cancel="$t('dashboard.device_detail.main_card.disable_renew_no')"
                :is-horizontally = 'isMobile'
                switchButton
            ></ReoDialog>
        </div>
        <SignPaypal
            :serviceId="subscriptionId"
            v-if='showItems.showDialogs.signPaypal'
            @cancel='cancelSign'
            @success="signSuccess"
        />
        <ReoDialog
            v-if="showItems.showDialogs.needPay"
            :labelOfConfirm="$t('dashboard.device_detail.main_card.autoRenewNeedPay.confirm')"
            @confirm="$emit('renew')"
            @cancel="showItems.showDialogs.needPay = false"
        >
            <div class="needPay">
                {{ $t('dashboard.device_detail.main_card.autoRenewNeedPay.text') }}
            </div>
        </ReoDialog>
        <ReoDialog
            v-if="showItems.showDialogs.choosePayment"
            :title="$t('dashboard.device_detail.main_card.chooseSignType')"
            @confirm="confirmSign()"
            @cancel="cancelChooseSign"
            :labelOfConfirm="showNextButton ? $t('common.next') : ''"
            class="choosePaymentDialog"
            buttonGroupClass="paymentButton"
            titleClass="choosePaymentDialogTitle"
        >
            <div class="signTypeChooseContent">
                <checkbox-group
                    :options="paymentOptions"
                    v-model="selectedPayment"
                    @change="signTypeChange"
                />

                <div class="adyenSchemeWrapper" v-show="selectedPayment === creditCardSignKey">
                    <Processing v-if="loadingAdyenComponent" loadingClass="processLoading"></Processing>
                    <div id="creditCartSignPlaceHolder" />
                </div>
                <div class="error" v-if="signError">{{ signError }}</div>
            </div>
        </ReoDialog>
        <ReoDialog
            v-if="showItems.showDialogs.adyenSignDialogLoading"
            class="loadingAdyenDialog"
            :showCloseIcon="false"
        >
            <div class="dialogContent">
                <Processing loadingClass="processLoading"></Processing>
            </div>
        </ReoDialog>
    </div>
</template>

<script lang="ts">
import { getBillingAgreementList, getPaymentsMethods } from '@/api/payment';
import { setAutoRenewal } from '@/api/subscriptions';
import { formatTime } from '@/common/js/date';
import { unfoldError } from '@/common/js/notice';
import { AxiosError } from 'axios';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import SignPaypal from './SignPaypal.vue';
import { Switch as ElSwitch } from 'element-ui';
import ReoDialog from '@/basic/Dialog.vue';
import ReoLink from '@/basic/Link.vue';
import { EPaymentMethod, IBillingAgreementItem, IPaymentMethod, IGetPaymentMethodsRes } from '@reolink-cloud/interface';
import { IAdyenStyle } from '@/lib/PaymentMethods';
import { createAdyenInstance } from '@/utils/adyenFactory';
import { AdyenPayment } from '@reolink-web/payment-sdk';
import CheckboxGroup from '@/basic/CheckboxGroup.vue';
import Processing from '../basic/processing.vue';
import * as Utils from '@reolink-web/utils';
import { apmMonitor } from '@/service/apmMonitor';
import { EApmTransactionType } from '@/config/apm';

export default Vue.extend({
    components: {
        SignPaypal,
        ElSwitch,
        ReoDialog,
        ReoLink,
        CheckboxGroup,
        Processing,
    },
    props:{
        autoRenewal: Boolean,
        nextBillingAt: Number,
        subscriptionId: String,
    },
    data() {

        return {
            showItems: {
                showDialogs: {
                    autoRenew: false,
                    disableAutoRenew: false,
                    signPaypal: false,
                    needPay: false,
                    choosePayment: false,
                    /** adyen认证确认弹窗 */
                    adyenSignDialogLoading: false,
                }
            },
            selectedPayment: 'paypal',
            paymentMethods: undefined as undefined | IGetPaymentMethodsRes,
            adyenInstance: undefined as undefined | AdyenPayment,
            adyenWebComponent: undefined as Awaited<ReturnType<AdyenPayment['create']>>,
            loadingAdyenComponent: false,
            signError: '',
            shouldStopAdyenPolling: false,
            showNextButton: true,
            /** 信用卡签约key */
            creditCardSignKey: 'credit_card'
        };

    },
    computed: {
        ...mapGetters( ['isMobile'] as const ),
        nextBillingAtFormatted(): string {

            return formatTime( this.nextBillingAt ?? 0 );

        },
        adyenPaymentMethods(): IPaymentMethod | undefined {

            return this.paymentMethods?.items.find( method => method.gateway === EPaymentMethod.ADYEN );

        },
        paymentOptions(): Array<{ label: string; value: string; }> {

            const options: Array<{ label: string; value: string; }> = [];

            const canPaypalAutoRenew = !!this.paymentMethods?.items.find( item => item.gateway === EPaymentMethod.PAYPAL && item.status === 'enable' );

            if ( canPaypalAutoRenew ) {

                options.push({
                    label: this.$t( 'common.paypal' ) as string,
                    value: 'paypal',
                });

            }

            // 包含adyen信用卡支付，且该支付方式已启用
            if ( this.paymentMethods?.rel.sort.includes( 'adyen.scheme' ) && this.adyenPaymentMethods?.status === 'enable' ) {

                options.push({
                    label: this.$t( 'common.creditCard' ) as string,
                    value: this.creditCardSignKey,
                });

            }

            return options;

        }
    },
    methods: {
        async handleAutoRenewChange( current: boolean ) {

            if ( current ) {

                if ( this.subscriptionId ) {

                    try {

                        apmMonitor.startOrRenameTransaction( this.transactionNames.LEVEL1_AUTO_RENEW_ENABLE, EApmTransactionType.USER_INTERACTION );
                        const { items }  = await getBillingAgreementList();
                        const hasEnabledPayment = items.find( item => item.status === 'enable' );
                        if ( !hasEnabledPayment ) {

                            if ( !this.paymentMethods ) {

                                this.paymentMethods = ( await getPaymentsMethods({
                                    'subscription-id': this.subscriptionId
                                }));

                            }

                            this.signError = '';
                            this.showItems.showDialogs.choosePayment = true;
                            this.selectedPayment = this.paymentOptions?.[0]?.value ?? '';

                            if ( this.selectedPayment === this.creditCardSignKey ) {

                                // 延迟加载adyen组件，避免页面节点未挂载进去
                                setTimeout(() => {

                                    this.loadAdyenComponent().catch( error => {

                                        console.error( error );

                                    }).finally(() => {

                                        this.loadingAdyenComponent = false;

                                    });

                                }, 0 );

                            }

                            return;

                        }

                    }
                    catch ( e ) {

                        console.error( e );
                        // 此处没有业务出来，仅做服务错误兜底
                        unfoldError( 'errors.server_error' );

                    }

                    try {

                        await setAutoRenewal( this.subscriptionId, 'enable' );
                        this.$emit( 'update:autoRenew', true );
                        this.$emit( 'refresh' );

                    } catch ( e ) {

                        this.dealWithAutoRenewalError( e );

                    }

                }

            } else {

                this.showItems.showDialogs.disableAutoRenew = true;

            }

        },
        /** 处理开启订阅自动续费的错误 */
        dealWithAutoRenewalError( e: unknown ) {

            const symbol = ( e as AxiosError ).response?.data.error.symbol;
            const reason = ( e as AxiosError ).response?.data.error.metadata.reason;
            if ( symbol === 'cannot_enable_auto_renewal' ) {

                if ( reason === 'cannot enable auto renewal for unpaid subscription' ) {

                    this.showItems.showDialogs.needPay = true;

                } else if ( reason === 'last order country is not auto renewable' ) {

                    unfoldError( 'errors.auto_renew_country_not_support"' );

                } else {

                    unfoldError( 'dashboard.device_detail.main_card.autoRenewFail' );

                }

            } else if ( symbol === 'auto_renewal_not_supported' ) {

                unfoldError( 'dashboard.device_detail.main_card.autoRenewNotSupport' );

            } else {

                unfoldError( 'dashboard.device_detail.main_card.autoRenewFail' );

            }

        },
        async disableAutoRenewal() {

            if ( this.subscriptionId ) {

                this.showItems.showDialogs.disableAutoRenew = false;
                apmMonitor.startOrRenameTransaction( this.transactionNames.LEVEL1_AUTO_RENEW_DISABLE, EApmTransactionType.USER_INTERACTION );
                try {

                    await setAutoRenewal( this.subscriptionId, 'disable' );
                    this.$emit( 'update:autoRenew', false );
                    this.$emit( 'refresh' );

                } catch ( e ) {

                    console.error( e );
                    unfoldError( 'dashboard.device_detail.main_card.disable_renew_err' );

                }

            }

        },
        async signSuccess() {

            this.showItems.showDialogs.signPaypal = false;
            await this.handleAutoRenewChange( true );

        },
        cancelSign() {

            this.showItems.showDialogs.signPaypal = false;

        },
        cancelChooseSign() {

            this.showItems.showDialogs.choosePayment = false;
            this.selectedPayment = 'paypal';
            this.signError = '';
            this.showNextButton = true;

        },
        signTypeChange() {

            this.signError = '';
            this.showNextButton = true;

            if ( this.selectedPayment === this.creditCardSignKey ) {

                this.loadAdyenComponent().catch( error => {

                    console.error( error );

                }).finally(() => {

                    this.loadingAdyenComponent = false;

                });

            }

        },
        confirmSign() {

            if ( !this.selectedPayment ) {

                return;

            }

            if ( this.selectedPayment === 'paypal' ) {

                this.showItems.showDialogs.signPaypal = true;
                this.cancelChooseSign();

            }
            else {

                this.signError = '';

                this.adyenInstance?.trigger( 'scheme' );

            }

        },
        /** 轮询后端接口，等待adyen webhook回调后，再显示成功 */
        async waitAdyenToken() {

            this.shouldStopAdyenPolling = false;

            await Utils.Async.retry({
                callback: async () => {

                    let items: IBillingAgreementItem[] = [];

                    try {

                        const res  = await getBillingAgreementList();

                        items = res.items;

                    }
                    catch ( e ) {

                        this.shouldStopAdyenPolling = true;
                        throw e;

                    }

                    const hasAdyenPayment = items.find( item => item.gateway === 'adyen' && item.status === 'enable' );

                    return hasAdyenPayment ? Promise.resolve( 1 ) : Promise.reject( 0 );

                },
                // 产品要求一直等待重试
                maxTimes: -1,
                strategy: () => {

                    return 3000;

                },
                isRetryable: () => {

                    return !this.shouldStopAdyenPolling;

                }
            });

        },
        /**
         * 加载adyen信用卡支付组件
         */
        async loadAdyenComponent() {

            // 卸载adyen web component，避免重复加载
            this.adyenWebComponent?.unmount();

            const adyenStyle = this.adyenPaymentMethods!.style as unknown as IAdyenStyle;

            this.loadingAdyenComponent = true;

            this.adyenInstance = await createAdyenInstance({
                clientKey: adyenStyle.clientKey,
                environment: adyenStyle.region,
                paymentMethodsResponse: {
                    paymentMethods: adyenStyle.paymentMethods
                },
                showPayButton: false,
            }, adyenStyle, {
                subscriptionId: this.subscriptionId!,
                onSuccess: async () => {

                    this.cancelChooseSign();
                    // 等待adyen webhook回调后，再显示成功
                    this.showItems.showDialogs.adyenSignDialogLoading = true;

                    try {

                        await this.waitAdyenToken();

                    }
                    catch ( e ) {

                        console.error( e );
                        unfoldError( 'dashboard.autoRenewal.autoRenewalSignFailed' );
                        return;

                    }
                    finally {

                        this.showItems.showDialogs.adyenSignDialogLoading = false;

                    }

                    try {

                        await setAutoRenewal( this.subscriptionId!, 'enable' );
                        this.$emit( 'update:autoRenew', true );
                        this.$emit( 'refresh' );

                    } catch ( e ) {

                        this.dealWithAutoRenewalError( e );

                    }

                },
                showDefaultError: false,
                onError: ( _, errorMsg ) => {

                    this.signError = errorMsg;

                },
                onAction: ( action ) => {

                    if ( action ) {

                        // 3D验证弹窗出现时，隐藏下一步按钮
                        this.showNextButton = false;

                    }

                },
                onAdditionalDetails: () => {

                    this.showNextButton = true;

                }
            });

            this.adyenWebComponent = await this.adyenInstance?.create(
                'scheme',
                '#creditCartSignPlaceHolder',
                {
                    hasHolderName: true,
                    holderNameRequired: true,
                    /** 此处可能是在后端api上设置store details */
                    // enableStoreDetails: true,
                }
            );

        }
    },
});
</script>

<style lang="less" scoped>
.device_detail_main_card_bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: @bg_01;
    padding: 12px;
    margin-top: 8px;
    border-radius: @border-radius-level-button;
    box-sizing: border-box;
    width: 100%;

    .device_detail_main_card_bottom_left {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-family: @font_02;
        line-height: normal;
        letter-spacing: 0em;
        p:first-of-type{
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .device_detail_main_card_bottom_left_title {
            font-size: 16px;
            font-weight: 500;
            color: #00104A;
            @media @mobile{
                font-size: 14px;
            }
        }

        .device_detail_main_card_bottom_left_content {
            font-size: 16px;
            font-weight: normal;
            color: #00104A;
            @media @mobile{
                font-size: 14px;
            }
        }
    }
}
.needPay{
    font-size: 16px;
}
.paymentGroup {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
    min-height: 100px;
}
.reo-dialog-inner {
    width: 400px;
}
.signTypeChooseContent {
    min-width: 484px;
    padding: 0px 2px;

    @media @mobile {
        min-width: unset;
    }
}

.dialogContent {
    min-width: 420px;
    @media @mobile {
        min-width: unset;
    }
}

.choosePaymentDialog {

    font-size: 16px;

    :deep(.paymentButton) {

        padding-right: 2px;
    }

    :deep(.choosePaymentDialogTitle) {
        padding-left: 2px;
    }

    // 弹窗内容高度无限制，由内容撑开
    :deep(.reo-dialog-inner) {
        max-height: unset !important;
    }

    // 防止adyen加载状态的视图高度太小，加载图标溢出，导致弹窗内容高度溢出，出现滚动条
    :deep(.adyen-checkout__spinner__wrapper) {
        min-height: 72px;
    }

    .adyenSchemeWrapper {
      margin-top: 24px;
      width: 480px;

      @media @mobile {
        margin-top: 16px;
        width: unset;
      }
    }

    .error {
        color: @red_5;
        font-size: 14px;
        text-align: left;
        margin-top: 8px;
    }
}

.loadingAdyenDialog,
.choosePaymentDialog {
    :deep(.processLoading) {
        background: transparent;
        min-height: 200px;
    }
}

/** payment sdk会支付按钮，此处业务不需要默认的支付按钮 */
.signTypeChooseContent :deep(#reo-adyen-scheme-btn) {
    display: none;
}
</style>
